import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { WorkflowService } from '../../workflow.service';
import { WorkFlowCategoryOwner } from '../../workflow.model';
import {
  AddIconComponent,
  ChevronDownIconComponent,
  DeleteIconComponent,
} from '@kuiper/icons';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { PaginatorComponent } from '../../../../../../ui-components/lib/paginator/paginator.component';
import { ModalServiceService } from '../../../../../../ui-components/lib/modal/modal-service.service';
import { ToasterService } from '../../../../../../ui-components/services/toast/toast.service';
import { GroupUserCardComponent } from '../../../../../common/components/group-user-card/group-user-card.component';
import { OverlayLoaderComponent } from '../../../../../common/components/overlay-loader/overlay-loader.component';
import { AssignGroupUsersComponent } from '../../../../../common/components/assign-group-users/assign-group-users.component';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { LookupService } from '../../../../../common/services/lookup/lookup.service';
import { ActionList } from '../../../../../common/models/action-list.model';
import { Option } from '../../../../../common/models/option.model';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { FeatureFlagsService } from '../../../../../common/services/feature-flags/feature-flags.service';

@Component({
  selector: 'lib-workflow-category-details',
  imports: [
    BadgeComponent,
    GroupUserCardComponent,
    PaginatorComponent,
    TranslocoPipe,
    OverlayLoaderComponent,
    AssignGroupUsersComponent,
    ChevronDownIconComponent,
    DeleteIconComponent,
    AddIconComponent,
  ],
  templateUrl: './workflow-category-details.component.html',
  styleUrl: './workflow-category-details.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class WorkflowCategoryDetailsComponent implements OnInit {
  route = inject(ActivatedRoute);
  router = inject(Router);
  toastService = inject(ToasterService);
  workflowService = inject(WorkflowService);
  modalService = inject(ModalServiceService);
  userService = inject(LookupService);
  transloco = inject(TranslocoService);
  featureFlagsService = inject(FeatureFlagsService);
  tenantService = inject(TenantService);

  assignOwnersDialog = viewChild<TemplateRef<unknown>>('assignOwnersDialog');
  removeUserModal = viewChild<TemplateRef<unknown>>('removeOwner');

  currentPage = signal<number>(1);
  currentSize = signal<number>(15);
  totalPages = signal<number>(20);
  isLoading = signal<boolean>(false);
  isRemoveUserLoading = signal<boolean>(false);
  totalOwnersCount = signal<string>('');
  selectedUsersId = signal<number[]>([]);
  selectedCategoryId = signal<string>('');
  isAssignOwnersLoading = signal<boolean>(false);
  modalCurrentPage = signal<number>(1);
  modalTotalPages = signal<number>(20);
  groupUsersCards = signal<Option[]>([]);
  isLoadingGroupUsers = signal<boolean>(false);
  selectedUsers = signal<Option[]>([]);
  selectedUsersIds = signal<string[]>([]);
  categoryDetails = signal<{ id: string; name: string }>({ id: '', name: '' });
  categoryOwners = signal<WorkFlowCategoryOwner[]>([]);
  mappedCategoryOwners = signal<Option[]>([]);

  subscribe = new Subject();

  actionList: ActionList[] = [
    {
      key: 'view_details',
      hasIcon: true,
      label: this.transloco.translate('view_details_action_item'),
      hasDivider: false,
      labelCustomStyle: '',
      hasToggle: false,
      toggleValue: false,
    },
    {
      key: 'remove',
      hasIcon: true,
      label: this.transloco.translate('remove_action_item'),
      hasDivider: false,
      labelCustomStyle: 'text-destructive-500 font-medium text-xs',
      hasToggle: false,
      toggleValue: false,
    },
  ] as ActionList[];

  constructor() {
    this.route.params.subscribe((params: any) => {
      this.selectedCategoryId.set(params['id']);
    });
  }

  ngOnInit() {
    this.getCategoryDetails();
    this.getCategoryOwners();
  }

  openAssignOwnersDialog() {
    this.currentPage.set(1);
    this.getUsers();

    this.modalService.modalContent.set({
      isOpen: true,
      customizeContent: this.assignOwnersDialog(),
      canNotCloseFromOutside: true,
      width: '50.5',
    });
  }

  getUsers(name = '', page = 1) {
    this.modalCurrentPage.set(page);
    this.isLoadingGroupUsers.set(true);
    this.userService
      .userLookup({
        name,
        page: this.modalCurrentPage(),
        filtersRole: '',
        filtersNotPermissionForResource: 'own',
        filtersNotPermissionForResourceResourceType: 'flow_template_category',
        filtersNotPermissionForResourceResourceId: this.selectedCategoryId(),
        filtersNotState: 'inactive',
        filtersNotStateCancelled: 'cancelled',
      })
      .subscribe(response => this.handleUsersResponse(response));
  }

  handleUsersResponse(response: any) {
    const mappedData = response.data.map((user: any) => ({
      ...user,
      isSelected: this.selectedUsers().some(
        selectedUser => selectedUser.value === user.value
      ),
    }));

    if (this.modalCurrentPage() !== 1) {
      this.groupUsersCards().push(...mappedData);
    } else {
      this.groupUsersCards.set(mappedData);
    }
    this.modalTotalPages.set(response.meta.last_page);
    this.isLoadingGroupUsers.set(false);
  }

  clearSelectedUsers() {
    this.selectedUsers.set([]);
    this.selectedUsersIds.set([]);
  }

  onSelectGroupUser(user: Option) {
    user.isSelected = true;
    this.selectedUsers().unshift(user);

    this.groupUsersCards.set(
      this.groupUsersCards().filter(card => card.value !== user.value)
    );

    if (
      this.modalCurrentPage() < this.modalTotalPages() &&
      this.groupUsersCards().length < 7
    ) {
      this.getUsers('', this.modalCurrentPage() + 1);
    }
  }

  removeSelectedGroupUser(user: Option) {
    user.isSelected = false;
    this.selectedUsers.set(
      this.selectedUsers().filter(card => card.value !== user.value)
    );

    this.groupUsersCards().unshift(user);
  }

  updateSelectedUserIds() {
    this.selectedUsersIds().push(
      ...this.selectedUsers().map(user => user.value)
    );
  }

  onSubmitAssignedGroupUsers() {
    this.isAssignOwnersLoading.set(true);
    this.updateSelectedUserIds();
    this.workflowService
      .updateFlowTemplateCategory(this.selectedCategoryId(), {
        added_owners: this.selectedUsersIds(),
      })
      .subscribe({
        next: () => {
          this.getCategoryOwners();
        },
        error: (error: any) => {
          this.toastService.add(
            error.message,
            '',
            'assets/images/icons/error.svg',
            'error'
          );
        },
        complete: () => {
          this.isAssignOwnersLoading.set(false);
          this.closeModal();
          this.clearSelectedUsers();
        },
      });
  }

  onRemoveUser() {
    this.isRemoveUserLoading.set(true);
    this.workflowService
      .updateFlowTemplateCategory(this.selectedCategoryId(), {
        removed_owners: this.selectedUsersId(),
      })
      .subscribe({
        next: () => {
          this.getCategoryOwners();
        },
        complete: () => {
          this.isRemoveUserLoading.set(false);
          this.closeModal();
        },
      });
  }

  handleCategoryOwners(res: any) {
    this.mappedCategoryOwners.set(
      res.map((user: any) => {
        return {
          label: user.name,
          value: user.id.toString(),
          image: user.image,
          email: user.email,
        };
      })
    );
  }

  actionListItemClick(event: any) {
    if (event.action == 'remove') {
      this.selectedUsersId.set([Number(event.item.value)]);
      this.openModal();
    } else {
      this.router.navigateByUrl(
        this.tenantService.adminPath(`users-database/${event.item.value}`)
      );
    }
  }

  handlePagination($event: number) {
    this.currentPage.set($event);
    this.getCategoryOwners();
  }

  openModal() {
    const modal = {
      isOpen: true,
      hasCloseIcon: false,
      customizeContent: this.removeUserModal(),
      width: '24',
    };

    this.modalService.modalContent.set(modal);
  }

  closeModal() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  goBack() {
    history.back();
  }

  getCategoryDetails() {
    this.workflowService
      .getWorkFlowCategoryDetails(this.selectedCategoryId())
      .subscribe({
        next: (res: any) => {
          this.categoryDetails.set(res.data);
        },
        error: () => {
          this.router.navigateByUrl(
            this.tenantService.adminPath(
              `${ADMIN_ROUTES.WORKFLOW.OWNER_CATEGORY.LIST}`
            )
          );
        },
        complete: () => {
          this.isLoading.set(false);
        },
      });
  }

  getCategoryOwners() {
    this.isLoading.set(true);
    this.workflowService
      .getWorkFlowCategoryOwners(
        this.selectedCategoryId(),
        this.currentPage(),
        this.currentSize(),
      )
      .subscribe({
        next: (res: any) => {
          this.categoryOwners.set(res.data);
          this.totalPages.set(res.meta.last_page);
          this.totalOwnersCount.set(res.meta.total);
          this.handleCategoryOwners(this.categoryOwners());
        },
        complete: () => {
          this.isLoading.set(false);
        },
      });
  }
}
